import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
    CheckCircle2,
    ArrowRight,
    Undo2,
    RefreshCw,
    BookCheck,
    AlertTriangle,
    PartyPopper,
} from 'lucide-react';
import * as YearEndService from '../../../../services/YearEndService';
import { formatSek } from '../constants';

function round2(n) { return Math.round(n * 100) / 100; }

/**
 * Step 9 — Efter stämman: book the stämma's resultatdisposition and wrap
 * up. The disposition verifikat lives on the stämma date — in the NEXT
 * fiscal year — so the step first shows whether that year exists and is
 * open (deep link to Inställningar → Bokföring when it's missing), then
 * the beslut summary and the one "Bokför" action. The backend's Swedish
 * 409/422 messages (stämma missing, already booked, no FY covering the
 * date) are shown verbatim. After booking: verifikat number, the
 * 2898-until-utbetalning explanation and a closing recap card; the
 * Bolagsverket and Deklaration steps carry their own filing confirmations.
 * The complete-step gate requires the booked disposition server-side.
 */
const PostStammaStep = ({
    companyId,
    processId,
    completed,
    onComplete,
    onUndo,
    busy,
    gateError,
}) => {
    const [data, setData] = useState(null); // { currentFiscalYear, nextFiscalYear, report }
    const [loadError, setLoadError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const [booking, setBooking] = useState(false);
    const [confirmUndoBooking, setConfirmUndoBooking] = useState(false);
    const [undoingBooking, setUndoingBooking] = useState(false);
    const [bookError, setBookError] = useState(null);
    const [bookedResult, setBookedResult] = useState(null); // POST response data

    // ---- Load -------------------------------------------------------------

    const fetchStatus = useCallback(async ({ silent = false } = {}) => {
        if (!processId) return;
        if (!silent) setRefreshing(true);
        setLoadError(null);
        try {
            const resp = await YearEndService.getPostStamma(companyId, processId);
            setData(resp?.data || null);
        } catch (err) {
            setLoadError(err.response?.data?.message
                || 'Kunde inte hämta uppgifterna. Försök igen senare.');
        } finally {
            setRefreshing(false);
        }
    }, [companyId, processId]);

    useEffect(() => { fetchStatus({ silent: true }); }, [fetchStatus]);

    const report = data?.report || null;
    const nextFy = data?.nextFiscalYear || null;
    const nextFyOpen = Boolean(nextFy && nextFy.state === 'open');
    const stammaRecorded = ['stamma_held', 'completed'].includes(report?.status);
    const booked = Boolean(report?.disposition_verification_id) || Boolean(bookedResult);

    const decisions = report?.stamma_decisions || {};
    const disp = decisions.resultatdisposition || report?.resultatdisposition || null;
    const utdelning = round2(Number(disp?.utdelning) || 0);
    const balanseras = disp?.balanserasINyRakning != null
        ? round2(Number(disp.balanserasINyRakning))
        : null;

    // ---- Actions ----------------------------------------------------------

    const handleBook = async () => {
        setBooking(true);
        setBookError(null);
        try {
            const resp = await YearEndService.bookDisposition(companyId, processId);
            toast.success('Resultatdispositionen är bokförd som ett verifikat.');
            setBookedResult(resp?.data || { booked: true });
            await fetchStatus({ silent: true });
        } catch (err) {
            setBookError(err.response?.data?.message
                || 'Kunde inte bokföra resultatdispositionen.');
        } finally {
            setBooking(false);
        }
    };

    // ---- Renderers --------------------------------------------------------

    const renderNextYearStatus = () => (
        <div className="ye-ps-section">
            <h5 className="ye-section-heading">Nästa räkenskapsår</h5>
            {nextFyOpen ? (
                <div className="ye-ps-status ye-ps-status-ok">
                    <CheckCircle2 size={16} />
                    <span>
                        Nästa räkenskapsår ({nextFy.year}: {nextFy.start_date} – {nextFy.end_date})
                        finns och är öppet — dispositionen kan bokföras där.
                    </span>
                </div>
            ) : nextFy ? (
                <div className="ye-ps-status ye-ps-status-warn">
                    <AlertTriangle size={16} />
                    <span>
                        Nästa räkenskapsår ({nextFy.year}) finns men är låst. Öppna det
                        under{' '}
                        <Link to={`/${companyId}/settings/fiscal-years`}>
                            Inställningar → Bokföring
                        </Link>{' '}
                        innan dispositionen bokförs.
                    </span>
                </div>
            ) : (
                <div className="ye-ps-status ye-ps-status-warn">
                    <AlertTriangle size={16} />
                    <span>
                        Det finns inget nästa räkenskapsår ännu. Dispositionen bokförs på
                        stämmodatumet, som ligger i det nya året — skapa det under{' '}
                        <Link to={`/${companyId}/settings/fiscal-years`}>
                            Inställningar → Bokföring
                        </Link>{' '}
                        först.
                    </span>
                </div>
            )}
        </div>
    );

    const renderDisposition = () => (
        <div className="ye-ps-section">
            <h5 className="ye-section-heading">Stämmans beslut om resultatet</h5>
            {!stammaRecorded ? (
                <div className="ye-well" style={{ marginTop: 0 }}>
                    Stämman är inte registrerad ännu — gå tillbaka till steget
                    Underskrifter &amp; årsstämma först.
                </div>
            ) : !disp ? (
                <div className="ye-well" style={{ marginTop: 0 }}>
                    Stämmans resultatdisposition saknas.
                </div>
            ) : (
                <>
                    <div className="ye-tax-rows">
                        <div className="ye-tax-row">
                            <span>Till förfogande enligt stämman</span>
                            <span className="ye-num">{formatSek(disp.tillDisposition)}</span>
                        </div>
                        <div className="ye-tax-row">
                            <span>Utdelning till aktieägarna</span>
                            <span className="ye-num">{formatSek(utdelning)}</span>
                        </div>
                        <div className="ye-tax-row ye-tax-row-group">
                            <span>Balanseras i ny räkning</span>
                            <span className="ye-num">
                                {formatSek(balanseras ?? round2((Number(disp.tillDisposition) || 0) - utdelning))}
                            </span>
                        </div>
                    </div>
                    <div className="ye-well">
                        Bokföringen flyttar årets resultat från konto 2099 enligt
                        stämmans beslut: utdelningen till 2898 (beslutad, ej utbetald
                        utdelning) och resten till 2091 (balanserat resultat).
                        Verifikatet bokförs på stämmodatumet — i det nya räkenskapsåret.
                    </div>
                    {bookError && (
                        <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                            {bookError}
                        </div>
                    )}
                    <div className="ye-proposal-actions">
                        <button
                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                            onClick={handleBook}
                            disabled={booking}
                        >
                            <BookCheck size={16} />
                            {booking ? 'Bokför…' : 'Bokför resultatdispositionen'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );

    const handleUndoBooking = async () => {
        setUndoingBooking(true);
        try {
            const resp = await YearEndService.undoBookDisposition(companyId, processId);
            toast.success(`Dispositionen är ångrad med rättelseverifikat ${resp?.data?.reversal || ''}.`);
            if (resp?.data?.note) toast.info(resp.data.note);
            setBookedResult(null);
            setConfirmUndoBooking(false);
            await fetchStatus({ silent: true });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte ångra dispositionen.');
        } finally {
            setUndoingBooking(false);
        }
    };

    const renderBookedState = () => (
        <div className="ye-ps-section">
            <h5 className="ye-section-heading">Stämmans beslut om resultatet</h5>
            <div className="ye-proposal-head">
                <span className="ye-badge ye-badge-success">
                    <CheckCircle2 size={13} />
                    Bokförd
                </span>
                {bookedResult?.verification && (
                    <span className="ye-proposal-title">
                        Verifikat <strong>{bookedResult.verification}</strong>
                    </span>
                )}
            </div>
            <div className="ye-tax-rows" style={{ marginTop: '0.75rem' }}>
                {bookedResult?.aretsResultat != null && (
                    <div className="ye-tax-row">
                        <span>Omfört årets resultat</span>
                        <span className="ye-num">{formatSek(bookedResult.aretsResultat)}</span>
                    </div>
                )}
                <div className="ye-tax-row">
                    <span>Utdelning enligt stämmobeslut</span>
                    <span className="ye-num">{formatSek(bookedResult?.utdelning ?? utdelning)}</span>
                </div>
            </div>
            {(bookedResult?.utdelning ?? utdelning) > 0 && (
                <div className="ye-well">
                    Utdelningen ligger på konto 2898 (beslutad, ej utbetald utdelning)
                    tills den betalas ut — bokför själva utbetalningen mot bankkontot
                    när pengarna förs över till ägarna.
                </div>
            )}
            {!confirmUndoBooking ? (
                <button
                    className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2 mt-3"
                    onClick={() => setConfirmUndoBooking(true)}
                    disabled={undoingBooking}
                >
                    <Undo2 size={14} />
                    Ångra bokföringen
                </button>
            ) : (
                <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: '0.875rem', maxWidth: 560 }}>
                    Dispositionen nollställs med ett rättelseverifikat — inget raderas ur
                    bokföringen. Vill du fortsätta?
                    <div className="d-flex gap-2 mt-2">
                        <button
                            className="btn btn-danger btn-sm"
                            onClick={handleUndoBooking}
                            disabled={undoingBooking}
                        >
                            {undoingBooking ? 'Ångrar…' : 'Ja, ångra dispositionen'}
                        </button>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setConfirmUndoBooking(false)}
                            disabled={undoingBooking}
                        >
                            Avbryt
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    const renderClosingCard = () => (
        <div className="ye-ps-done">
            <div className="ye-ps-done-icon"><PartyPopper size={26} /></div>
            <div className="ye-ps-done-title">Bokslutet är klart 🎉</div>
            <ul className="ye-ps-done-list">
                <li>
                    <CheckCircle2 size={15} />
                    Året är avstämt, skatten är bokförd och räkenskapsåret är låst
                </li>
                <li>
                    <CheckCircle2 size={15} />
                    Årsredovisningen är upprättad och underskriven
                </li>
                <li>
                    <CheckCircle2 size={15} />
                    Årsstämman är hållen och protokollförd
                </li>
                <li>
                    <CheckCircle2 size={15} />
                    Resultatdispositionen är bokförd enligt stämmans beslut
                </li>
            </ul>
            <p className="ye-ps-done-note">
                Inlämningarna hanteras i sina egna steg: årsredovisningen till
                Bolagsverket i steget Bolagsverket (nedladdning + bekräftelse) och
                inkomstdeklarationen i steget Deklaration (SRU-filer + bekräftelse).
                Själva överföringen gör du hos myndigheterna tills de digitala
                anslutningarna finns på plats.
            </p>
        </div>
    );

    const renderBody = () => {
        if (loadError) {
            return (
                <>
                    <div className="alert alert-danger" style={{ fontSize: '0.875rem' }}>
                        {loadError}
                    </div>
                    <button
                        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
                        onClick={() => fetchStatus()}
                        disabled={refreshing}
                    >
                        <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
                        Försök igen
                    </button>
                </>
            );
        }
        if (!data) {
            return (
                <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                    Hämtar uppgifterna…
                </p>
            );
        }
        if (booked) {
            return (
                <>
                    {renderBookedState()}
                    {renderClosingCard()}
                </>
            );
        }
        return (
            <>
                {renderNextYearStatus()}
                {renderDisposition()}
            </>
        );
    };

    // ---- Layout -----------------------------------------------------------

    return (
        <>
            <style>{`
                .ye-ps-section + .ye-ps-section { margin-top: 1.75rem; }
                .ye-ps-status {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.6rem;
                    padding: 0.75rem 1rem;
                    border-radius: 8px;
                    font-size: 0.875rem;
                    line-height: 1.5;
                }
                .ye-ps-status svg { flex-shrink: 0; margin-top: 2px; }
                .ye-ps-status-ok {
                    background: #f0fdf4;
                    border: 1px solid #bbf7d0;
                    color: #166534;
                }
                .ye-ps-status-warn {
                    background: #fffbeb;
                    border: 1px solid #fde68a;
                    color: #92400e;
                }
                .ye-ps-status-warn a { color: #92400e; text-decoration: underline; font-weight: 600; }
                .ye-ps-done {
                    margin-top: 1.75rem;
                    border: 1px solid #bbf7d0;
                    background: #f0fdf4;
                    border-radius: 12px;
                    padding: 1.5rem 1.75rem;
                }
                .ye-ps-done-icon { color: #16a34a; margin-bottom: 0.5rem; }
                .ye-ps-done-title {
                    font-size: 1.125rem;
                    font-weight: 700;
                    color: #14532d;
                    margin-bottom: 0.75rem;
                }
                .ye-ps-done-list {
                    list-style: none;
                    margin: 0;
                    padding: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 0.4rem;
                }
                .ye-ps-done-list li {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.5rem;
                    font-size: 0.875rem;
                    color: #166534;
                }
                .ye-ps-done-list svg { flex-shrink: 0; margin-top: 2px; }
                .ye-ps-done-note {
                    margin: 0.9rem 0 0;
                    font-size: 0.8125rem;
                    color: #4b5563;
                    max-width: 64ch;
                    line-height: 1.6;
                }
            `}</style>

            <div className="modern-card-header ye-step-header">
                <div>
                    <h4 className="section-title">Efter stämman</h4>
                    <p className="section-subtitle">
                        Sista steget: stämmans beslut om resultatet bokförs — till
                        exempel utdelning till ägarna — och bokslutet avslutas. Vi
                        kontrollerar också att nästa räkenskapsår finns på plats.
                    </p>
                </div>
                <div className="action-buttons">
                    {booked ? (
                        <span className="ye-badge ye-badge-success">
                            <CheckCircle2 size={13} />
                            Dispositionen är bokförd
                        </span>
                    ) : (
                        <span className="ye-badge ye-badge-neutral">Inte bokförd ännu</span>
                    )}
                </div>
            </div>

            <div className="modern-card-body">
                {renderBody()}

                {gateError && (
                    <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                        <strong>{gateError.message}</strong>
                    </div>
                )}
            </div>

            <div className="ye-step-footer">
                {completed ? (
                    <>
                        <span className="ye-footer-done">
                            <CheckCircle2 size={18} style={{ color: 'var(--bs-success)' }} />
                            Bokslutet är avslutat
                        </span>
                        <button
                            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                            onClick={onUndo}
                            disabled={busy}
                        >
                            <Undo2 size={14} />
                            Ångra markeringen
                        </button>
                    </>
                ) : (
                    <>
                        <span className="ye-footer-hint">
                            {booked
                                ? 'Dispositionen är bokförd — markera steget som klart så är bokslutet avslutat.'
                                : 'Bokför resultatdispositionen först — sedan kan bokslutet avslutas.'}
                        </span>
                        <button
                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                            onClick={onComplete}
                            disabled={busy}
                        >
                            {busy ? 'Sparar…' : 'Markera som klart'}
                            <ArrowRight size={16} />
                        </button>
                    </>
                )}
            </div>
        </>
    );
};

export default PostStammaStep;
